import { useMemo, useState, useSyncExternalStore } from "react";
import type { DashboardEditorIndexedDBT } from "../../db";
import { createObjectStoreState } from "../../db/lib/stores";
import type { DashboardBlockModelT } from "../../schema/dashboard";
import type { DashboardModelT } from "../../schema/dashboard";
import { uniqBy } from "diagnostics-lib/array";
export const useEditorStore = (
  operations: DashboardEditorIndexedDBT["document"] | undefined,
  dashboardId: string | undefined,
) => {
  const [initialized, setInitialized] = useState(false);
  const store = useMemo(
    () =>
      operations
        ? createObjectStoreState<
            DashboardEditorIndexedDBT["document"],
            { dashboard: DashboardModelT; blocks: DashboardBlockModelT[] },
            DashboardModelT | DashboardBlockModelT
          >(operations, store =>
            Promise.all([
              store.get<DashboardModelT>(dashboardId ?? ""),
              store.all<DashboardBlockModelT>(dashboardId, "dashboardId"),
            ])
              .then(([{ result: dashboard }, { result: blocks }]) => ({
                dashboard,
                blocks: uniqBy([...blocks], "id").sort(
                  ({ position: aPosition }, { position: bPosition }) => aPosition[1] - bPosition[1],
                ),
              }))
              .finally(() => setInitialized(true)),
          )
        : null,
    [dashboardId, operations],
  );

  return useMemo(
    () => ({
      initialized,
      ...store,
    }),
    [initialized, store],
  );
};

export const useDashboardEditorState = (store: ReturnType<typeof useEditorStore> | undefined) =>
  useSyncExternalStore(store?.subscribe ?? (() => () => true), store?.snapshot ?? (() => null));
